import { SettingsNOC } from '~/interfaces/homeSecurity.interface';
import { Gateway } from '~/models/gateways/gateway.model';
import { NAModule } from '~/models/modules/NAModule.model';
import { NADevice } from '~/models/NADevice.model';

export interface DevicesState {
    devices: Device [];
    currentGatewayId: string;
    currentModuleId: string;
    deviceInformationArray: DeviceInformation[];
}

export type Device = NADevice & NAModule & Gateway;

export interface SpecFields {
    _id: string;
    device_group: {
        $id: string;
    };
    type: string;
    secret_key: string;
    hw_version: number;
    setup_date: {
        sec: number,
        usec: number;
    };
    last_connection_status: number;
    reset_next_install: boolean;
    last_reset: number;
    home_id: {
        $id: number;
    };
    status: string;
    firmware: number;
    homekit_status: string;
    name: string;
    sd_status: number;
    alim_status: number;
    station_name?: string;
}

export interface NetworkFields {
    vpn?: {
        nas_id: string;
        virtual_ip: string;
        connection_time: number;
        session_id: number;
        last_disconnection?: number;
        public_ip?: string;
    };
    ip?: string;
    connection?: number | string;
    rf_amb_status?: number;
    server_node?: ServerNode;
    network_lock?: boolean;
    wifi_status?: number;
    plug_connected_boiler?: string;
    netcom_transport?: string;
    udp_ip: string;
    udp_pusher: string;
    udp_port: string;
}

export interface ServerNode {
    host: string;
    connection: number;
    disconnection: number;
}

export interface ObjectDetectionSensibiliy {
    human: number;
    vehicle: number;
    animal: number;
}

export interface PlaceFields {
    altitude: number;
    city: string;
    country: string;
    improveLocProposed: boolean;
    location: [number, number];
    timezone: string;
    country_name: string;
    continent: string;
    street: string;
}

export type BehaviorKey = 'touch_disabled'
| 'led_disabled'
| 'legacy_offset'
| 'relay_polarity_inverted'
| 'rf_statistics_enabled'
| 'usb_host_disabled'
| 'modified_rf_cycle_duration';

export interface BehaviorFields {
    touch_disabled?: boolean;
    led_disabled?: boolean;
    legacy_offset?: boolean;
    relay_polarity_inverted?: boolean;
    rf_statistics_enabled?: boolean;
    usb_host_disabled?: boolean;
    modified_rf_cycle_duration?: boolean;
}

export interface SetBehaviorParams {
    device_id: string
    & BehaviorFields;
}

export interface DeviceData {
    // data we receive from API
    name: string;
    id: string;
    type: string;
    bridge?: string;
    modules_bridged?: string[];
    setup_date?: number;
    room_id?: string;
    is_module: boolean;
    device_group?: string;

    // data not received from API
    homeId?: string;
}

export interface DeviceInformation {
    product: string;
    start?: number;
    end?: number;
    list?: number[];
    description: string;
    criticality: string;
}

export interface DisplayedInfo {
    description: string;
    criticality: string;
}

export interface Operation {
    name: string;
    action: string;
    callback: (params?: any) => void;
    disabled?: { value: boolean, reason?: string };
    hidden?: boolean;
    tooltip?: string;
}

export type OperationCriticality = 'dangerous' | 'moderate' | 'harmless';

export interface Endpoint {
    id: string;
    bindings: string[];
}

// #TODO: better way to handle battery levels
export const BatteryLevel = {
    NtmoAPIBatteryLevel0: 5700,
    NtmoAPIBatteryLevel1: 5400,
    NtmoAPIBatteryLevel2: 5100,
    NtmoAPIBatteryLevel3: 4800,
};

export interface OffsetFields {
    temperature?: number;
    pressure?: number;
    humidity_offset?: number;
    humidity?: number;
}

export type OffsetKey = 'temperature'
| 'pressure'
| 'humidity_offset'
| 'humidity';

export interface SetOffsetParams {
    device_id: string;
    module_id: string;
    data_type: OffsetKey;
    offset: number;
}

export interface HeatingAlgoFields {
    last_compute_try?: number;
    time_pid_computed?: number;
    in_use: string;
    simple_algo?: number;
    simple_params?: HeatingAlgoSimpleParams;
    pid_algo?: number;
    pid_params?: HeatingAlgoPidParams;
    gefs?: ThermoregulationParams;
    station?: ThermoregulationParams;

}

export interface HeatingAlgoSimpleParams {
    high_deadband: number;
}

export interface HeatingAlgoPidParams {
    gain: number;
    ti: number;
    td: number;
    period: number;
}

export interface ThermoregulationParams {
    lag_up: number;
    lag_down: number;
    tau: number;
    off_overshoot: number;
    on_overshoot: number;
    pk: number;
}

export interface SetHeatingAlgoParams {
    device_id: string;
    algo_type: AlgoType;
    algo_params?: HeatingAlgoPidParams | HeatingAlgoSimpleParams;
}

export type AlgoType = 'pid_algo' | 'simple_algo';

export interface MonkeyFields {
    sensor_battery_voltage?: number;
    detection_chamber_status?: string;
    battery_alarm_state?: string;
    last_smoke_detected_start_time?: number;
    last_smoke_detected_end_time?: number;
    status_tampered?: boolean;
    sound_test?: string;
    last_sound_test?: number;
}

export interface NSDFields {
    battery_percent?: number;
    battery_voltage_max?: number;
    battery_voltage_min?: number;
    sound_test_reminder?: string;
    notify_wifi_connection?: boolean;
}

export interface NCOFields {
    battery_percent?: number;
    battery_voltage_max?: number;
    battery_voltage_min?: number;
    sound_test_reminder?: string;
    notify_wifi_connection?: boolean;
}

export interface TimelineEvent {
    time: number;
    type: string;
    session_id?: string;
    sub_type?: string;
    'Acct-Terminate-Cause': string;
    // custom info
    color?: string;
    duration?: string;
}

export interface DebugConfigs {
    home: {
        modules: Array<{ id: string, debug_enabled: boolean }>;
    };
}
export interface Diagnosis {
    home: {
        modules: Array<{ id: string, debug_enabled: boolean }>;
    };
}

export interface Command {
    customer_id?: string;
    order_id?: string;
    sku?: string;
    retailer_name?: string;
}

export interface MigrationData {
    target: string;
    sources: MigrationDataSources;
}

export type MigrationDataSources = Array<{
    bridge_id: string;
    modules?: string[];
}>;

export interface TransferDescription {
    bridge: string;
    target?: string;
    modules?: TransferModulesDescription[];
    rooms?: Array<{
        source_id: string;
        target_id: string;
    }>;
}

export interface TransferModulesDescription {
    source_id: string;
    target_id: string;
}

export interface TransferSourceItem {
    bridge_id: string;
    module_id?: string;
    disabled?: boolean;
}

export interface TransformedMigrationData {
    sources: TransferSourceItem[];
    target: string;
    selectedSource: TransferSourceItem | null;
}

export interface ImportDataParams {
    user_id: string;
    transfert_description: TransferDescription;
}

export enum ApplianceType {
    COOKING = 'cooking',
    RADIATOR = 'radiator',
    RADIATOR_WITHOUT_PILOT_WIRE = 'radiator_without_pilot_wire',
    WATER_HEATER = 'water_heater',
    ELECTRIC_CHARGER = 'electric_charger',
    LIGHT = 'light',
    FRIDGE_FREEZER = 'fridge_freezer',
    WASHING_MACHINE = 'washing_machine',
    TUMBLE_DRYER = 'tumble_dryer',
    DISHWASHER = 'dishwasher',
    MULTIMEDIA = 'multimedia',
    ROUTER = 'router',
    OTHER = 'other',
    OVEN = 'oven',
    SHUTTERS = 'shutters',
    CURTAINS = 'curtains',
    SHADE = 'shade',
    AIR_CONDITIONER = 'air_conditioner',
    GARAGE_DOOR = 'garage_door',
    GATE = 'gate',
    BSO = 'orientable_sun_shade',
    FAN = 'fan',
}

export enum SourceType {
    SOCKETS = 'sockets',
    LIGHTS = 'lights',
    HEATERS = 'heaters',
    COOKTOP = 'cooktop',
    OVEN = 'oven',
    ROLLER_SHUTTERS = 'roller_shutters',
    SANITARY_HOT_WATER = 'sanitary_hot_water',
    AIR_CONDITIONER = 'air_conditioner',
    COLD_WATER = 'cold_water',
    HOT_WATER = 'hot_water',
    GAS = 'gas',
    SOLAR_PANEL = 'solar_panel',
    OTHER = 'other',
}

export interface ObjectDetectionSensitivity {
    human: number;
    vehicle: number;
    animal: number;
}

export interface VisionRecognitionSensitivity {
    face_maybe: number;
    face_known: number;
}

export interface GetVpnConfig extends SettingsNOC {
    id: string;
    dropbox_config: number;
    object_detection_sensitivity?: ObjectDetectionSensitivity;
    vision_recognition_sensitivity?: VisionRecognitionSensitivity;
}
